// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslint:disable:no-big-function
// tslint:disable:no-identical-functions

import { ScrollOrientation } from '@epicuro-next/ataraxia/scroll-buttons';
import { dynamicElementsUserFeedbackKey } from '@epicuro-next/constants/dynamic-elements';
import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import {
  IDynamicItem,
  LogicOperator,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import {
  createInstanceWithChildren,
  getProvider,
} from '../../../utils/dynamic-items-utils';
import { PanelStatusEvent } from '../../types/panel-event';
import { header } from '../../views/header';
import { presetSelectHeader } from '../../views/preset-select-header';
import { createAnErrorOccurredElements } from '../error-occurred-elements';
import { createNoItemsElements } from '../no-items';
import { getScrollableContainer } from '../scrollable-container';

export const feedbackQuestionContainerId = 'feedback-question-container';

export function createScrollablePanel(
  orientation: ScrollOrientation = ScrollOrientation.horizontal,
) {
  const errorPanelStatusEvent = {
    provider: 'self',
    property: 'hasError',
    filter: function (hasError: boolean): boolean {
      return hasError;
    }.toString(),
    transform: function (): PanelStatusEvent {
      const event: PanelStatusEvent = {
        type: 'panel-status',
        data: 'hasError',
      };
      return event;
    }.toString(),
  };

  const noItemsPanelStatusEvent = {
    provider: 'self',
    property: 'hasNoItems',
    filter: function (hasNoItems: boolean): boolean {
      return hasNoItems;
    }.toString(),
    transform: function (): PanelStatusEvent {
      const event: PanelStatusEvent = {
        type: 'panel-status',
        data: 'hasNoData',
      };
      return event;
    }.toString(),
  };

  const scrollablePanelClasses = [
    'scrollable-panel-container',
    'd-block',
    'position-relative',
  ];
  if (orientation === 'vertical') {
    scrollablePanelClasses.push('h-100');
  }

  const scrollablePanel = createInstanceWithChildren({
    id: `scrollable-panel-container`,
    contentTypeId: 'dynamic-collections-renderer',
    if: undefined as IDynamicItem['if'],
    data: {
      backgroundColor: {
        provider: 'id:root',
        property: 'backgroundColor',
      },
      isLoading: {
        provider: 'id:root',
        property: 'productData',
        startWith: {
          value: 'isLoading',
        },
        transform: function (data: 'isLoading' | unknown[]): boolean {
          const isLoading = data === 'isLoading';
          return isLoading;
        }.toString(),
      },
      hasError: {
        provider: 'id:root',
        property: 'productData',
        transform: function (productData: unknown): boolean {
          return (
            productData === undefined ||
            window.neoclesDynamicFramework.recommendationsPanels.methods.isResolveValueError(
              productData,
            )
          );
        }.toString(),
      },
      hasData: {
        values: [
          {
            provider: 'id:root',
            property: 'productData',
          },
          { provider: 'self', property: 'hasError' },
        ],
        transform: function ([productData, hasErrorArg]: [
          unknown[],
          boolean,
        ]): boolean {
          if (hasErrorArg) {
            return false;
          }
          return productData && productData.length > 0;
        }.toString(),
        startWith: {
          value: [undefined, false],
        },
      },
      hasNoItems: {
        values: [
          { provider: 'self', property: 'isLoading' },
          { provider: 'self', property: 'hasData' },
          { provider: 'self', property: 'hasError' },
        ],
        transform: function ([isLoadingArg, hasDataArg, hasErrorArg]: [
          boolean,
          boolean,
          boolean,
        ]) {
          return !hasErrorArg && !isLoadingArg && !hasDataArg;
        }.toString(),
      },
    },
    styles: {
      classes: scrollablePanelClasses,
      style: {
        'background-color': { provider: 'self', property: 'backgroundColor' },
        __multiple__: {
          provider: 'id:root',
          property: 'panelStyle',
        },
        // tslint:disable-next-line:no-any
      } as { [key: string]: any },
      variables: {
        '--scrollable-sub-container-padding': '0',
      } as { [key: string]: string },
    },
    children: [] as Exclude<IDynamicItem['children'], undefined>,
    events: [noItemsPanelStatusEvent, errorPanelStatusEvent],
  });

  const scrollableContainer = getScrollableContainer(
    {
      isLoading: getProvider(scrollablePanel, 'isLoading'),
    },
    orientation,
  );

  const errorView: IDynamicItem = {
    ...createAnErrorOccurredElements({
      text: recommendationsPanelsLocaleKeys.anErrorOccurredWhileFetchingItems,
    }).container,
    if: ['EQ', [getProvider(scrollablePanel, 'hasError'), true]],
  };

  const noItemsView: IDynamicItem = {
    ...createNoItemsElements({}).container,
    if: ['EQ', [getProvider(scrollablePanel, 'hasNoItems'), true]],
    styles: {
      ...createNoItemsElements({}).container.styles,
      style: {
        'min-height': '150px',
      },
    },
  };

  const subContainer = createInstanceWithChildren({
    id: 'sub-container',
    styles: {
      classes: [] as string[],
      style: {},
    },
    contentTypeId: 'dynamic-collections-renderer',
    children: [scrollableContainer.scrollableContainer, noItemsView, errorView],
  });
  if (orientation === 'vertical') {
    subContainer.styles.classes = ['h-100', 'd-block'];
  }

  const filtersContainer = {
    id: 'filters-container',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['horizontal-panel-filters-container', 'mx-2', 'mt-1'],
    },
  };

  const headerContainer = {
    id: 'header-container',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: [
        'horizontal-panel-header-container',
        'd-flex',
        'justify-content-center',
      ],
      style: {
        __multiple__: {
          provider: 'id:root',
          property: 'headerContainerStyle',
        },
      },
    },
    children: [filtersContainer, header(), presetSelectHeader()],
  };

  scrollablePanel.children = [headerContainer, subContainer];

  const footerContainer = createInstanceWithChildren({
    id: 'footer-container',
    contentTypeId: 'dynamic-collections-renderer',
    if: ['EQ', [false, true]],
    styles: {
      classes: [
        'foter-container',
        'd-flex',
        'flex-row',
        'position-relative',
        'pb-2',
      ],
      style: {
        padding: '0 0.625rem',
        __multiple__: {
          provider: 'id:root',
          property: 'footerContainerStyle',
        },
      },
    },
    children: [],
  });
  scrollablePanel.children.push(footerContainer);

  const feedbackQuestionContainer: IDynamicItem = {
    id: feedbackQuestionContainerId,
    contentTypeId: dynamicElementsUserFeedbackKey,
    // enable by if: [LogicOperator.EQ, [true, true]] in merchandising-dynamic-panel-configs rule
    if: [LogicOperator.EQ, [true, false]],
    styles: {
      style: {
        position: 'absolute',
        width: 'auto',
        'max-width': '95%',
        top: '1%',
        left: '10px',
        right: 'auto',
      },
    },
  };

  scrollablePanel.children.push(feedbackQuestionContainer);

  return scrollablePanel;
}
